import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ReceiptText, Plus, Trash2, Calculator, ArrowLeft, Save, Loader2, Calendar, Globe2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SUPPORTED_CURRENCIES } from "../lib/money";
import { TAX_COUNTRIES, findCountry, findSubregime } from "../data/taxRegimes";

const INVOICE_TYPES = ["supplier_invoice", "purchase_order", "receipt",
                       "credit_note", "proforma", "delivery_note"];
const PAYMENT_METHODS = ["bank_transfer", "cheque", "cash", "card"];
const PAYMENT_STATUSES = ["unpaid", "partial", "paid", "overdue"];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso, n) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function emptyLine() {
  return { description: "", quantity: "", unit_price: "", line_total: "" };
}

export default function ManualInvoice() {
  const { t } = useTranslation("invoices");
  const navigate = useNavigate();
  const location = useLocation();
  // Pre-fill from location.state when navigated from the low-confidence banner
  const seed = location.state?.prefill || {};

  const [form, setForm] = useState({
    vendor_name: seed.vendor_name || "",
    invoice_number: seed.invoice_number || "",
    invoice_type: seed.invoice_type || "supplier_invoice",
    issue_date: seed.issue_date || todayIso(),
    due_date: seed.due_date || addDays(todayIso(), 30),
    subtotal: seed.subtotal ?? "",
    tax_amount: seed.tax_amount ?? "",
    tax_rate: seed.tax_rate ?? "",
    tax_label: seed.tax_label || "",
    total_amount: seed.total_amount ?? "",
    currency: seed.currency || "CAD",
    payment_status: seed.payment_status || "unpaid",
    payment_method: seed.payment_method || "",
    notes: seed.notes || "",
    line_items: seed.line_items?.length ? seed.line_items : [],
  });
  // Multi-country tax selector state (UI-only — gets reduced into
  // tax_rate / tax_amount / tax_label inside form on apply).
  const [taxCountry, setTaxCountry] = useState("CA");
  const [taxSubregimeId, setTaxSubregimeId] = useState(null);
  const [customTaxLabel, setCustomTaxLabel] = useState("");
  const [customTaxRate, setCustomTaxRate] = useState("");
  const [vendors, setVendors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Vendor tax-profile memory (iter60) — set after the debounced lookup, shown
  // briefly to the user and cleared 3s later. Holds the API response so the
  // applied regime keeps its own state (we don't want to re-apply on every
  // re-render).
  const [vendorProfileHint, setVendorProfileHint] = useState(null);
  const lastAppliedVendorRef = React.useRef("");

  const fromDocId = location.state?.fromDocId || null;

  // Vendor autocomplete — fetch known vendors on mount
  useEffect(() => {
    api.get("/invoices/vendors", { params: { limit: 100 } })
      .then((r) => setVendors(r.data.vendors || []))
      .catch(() => setVendors([]));
  }, []);

  // Vendor tax-profile memory (iter60) — when the user stops typing in the
  // vendor field for ~400ms, look up the last tax regime they used for this
  // supplier and pre-apply it (only when the user hasn't manually picked a
  // subregime yet, to avoid clobbering deliberate choices).
  useEffect(() => {
    const name = (form.vendor_name || "").trim();
    if (name.length < 3) {
      setVendorProfileHint(null);
      return;
    }
    if (lastAppliedVendorRef.current === name) return;
    const handle = setTimeout(async () => {
      try {
        const res = await api.get("/invoices/vendor-tax-profile",
          { params: { name } });
        const profile = res.data;
        // Don't override if the user has already actively picked a country.
        // The form starts on "CA" (default); we only auto-apply when no
        // explicit subregime has been selected yet.
        if (taxSubregimeId) {
          lastAppliedVendorRef.current = name;
          return;
        }
        if (profile.country_code) setTaxCountry(profile.country_code);
        // Currency: same no-overwrite rule as country onChange — only swap
        // when still on the CAD default.
        if (profile.currency && form.currency === "CAD") {
          setField("currency", profile.currency);
        }
        if (profile.tax_rate !== null && profile.tax_rate !== undefined) {
          const upd = recomputeAmountsFromRate(profile.tax_rate);
          setForm((f) => ({
            ...f,
            ...upd,
            tax_label: profile.tax_label || "",
          }));
        }
        if (profile.subregime_id) setTaxSubregimeId(profile.subregime_id);
        setVendorProfileHint({
          vendor_name: profile.vendor_name,
          tax_label: profile.tax_label,
          last_seen: profile.last_seen,
        });
        lastAppliedVendorRef.current = name;
        setTimeout(() => setVendorProfileHint(null), 3000);
      } catch {
        // 404 = no prior invoice; silently ignore.
        setVendorProfileHint(null);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.vendor_name]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Resolve country + subregime objects from state ids.
  const country = useMemo(() => findCountry(taxCountry), [taxCountry]);
  const subregime = useMemo(
    () => findSubregime(taxCountry, taxSubregimeId),
    [taxCountry, taxSubregimeId],
  );

  // Recompute tax_amount + total_amount from a given tax rate (%) and the
  // current subtotal. Returns the partial form update; the caller composes
  // it with the existing form state.
  const recomputeAmountsFromRate = (ratePct) => {
    const sub = parseFloat(form.subtotal);
    if (!isFinite(sub) || sub <= 0 || !isFinite(ratePct)) {
      return { tax_rate: ratePct };
    }
    const tax = Math.round(sub * (ratePct / 100) * 100) / 100;
    const total = Math.round((sub + tax) * 100) / 100;
    return { tax_rate: ratePct, tax_amount: tax, total_amount: total };
  };

  // Country change: optionally auto-switch the currency (only if the user
  // hasn't already moved away from the CAD default — see UX rules iter59).
  const onCountryChange = (newCode) => {
    setTaxCountry(newCode);
    setTaxSubregimeId(null);
    const c = findCountry(newCode);
    if (c && c.currency && form.currency === "CAD") {
      setField("currency", c.currency);
    }
    if (c?.exempt_by_default) {
      // USA — wipe pre-fills, the user will type the tax manually (or leave 0).
      setForm((f) => ({
        ...f,
        tax_rate: "",
        tax_amount: "",
        tax_label: "",
      }));
    }
  };

  const applySubregime = (sr) => {
    setTaxSubregimeId(sr.id);
    const upd = recomputeAmountsFromRate(sr.rate);
    setForm((f) => ({ ...f, ...upd, tax_label: sr.fixed_label || "" }));
  };

  const applyCustomTax = () => {
    const rate = parseFloat(customTaxRate);
    const label = customTaxLabel.trim();
    if (!label) {
      toast.error(t("manual_form.tax_selector.custom_label_required"));
      return;
    }
    if (!isFinite(rate) || rate < 0) {
      toast.error(t("manual_form.tax_selector.custom_rate_invalid"));
      return;
    }
    const upd = recomputeAmountsFromRate(rate);
    setForm((f) => ({ ...f, ...upd, tax_label: label }));
    toast.success(t("manual_form.tax_selector.custom_applied"));
  };

  const validate = () => {
    const e = {};
    if (!form.vendor_name?.trim()) e.vendor_name = t("manual_form.errors.vendor_required");
    const total = parseFloat(form.total_amount);
    if (!isFinite(total) || total <= 0) e.total_amount = t("manual_form.errors.total_required");
    if (form.payment_status !== "paid" && !form.due_date) {
      e.due_date = t("manual_form.errors.due_date_required_unpaid");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        vendor_name: form.vendor_name.trim(),
        invoice_number: form.invoice_number?.trim() || null,
        invoice_type: form.invoice_type,
        issue_date: form.issue_date || null,
        due_date: form.due_date || null,
        subtotal: form.subtotal === "" ? null : parseFloat(form.subtotal),
        tax_amount: form.tax_amount === "" ? null : parseFloat(form.tax_amount),
        tax_rate: form.tax_rate === "" ? null : parseFloat(form.tax_rate),
        tax_label: form.tax_label?.trim() || null,
        total_amount: parseFloat(form.total_amount),
        currency: form.currency,
        payment_status: form.payment_status,
        payment_method: form.payment_method || null,
        notes: form.notes?.trim() || null,
        line_items: form.line_items
          .filter((li) => (li.description || "").trim())
          .map((li) => ({
            description: li.description.trim(),
            quantity: li.quantity === "" ? null : parseFloat(li.quantity),
            unit_price: li.unit_price === "" ? null : parseFloat(li.unit_price),
            line_total: li.line_total === "" ? null : parseFloat(li.line_total),
          })),
      };
      const r = await api.post("/invoices/manual", payload);
      const docId = r.data.doc_id;
      const fired = r.data.alerts?.kinds?.length || 0;
      toast.success(
        fired > 0
          ? `${t("manual_form.success.created")} — ${t("manual_form.success.alerts_fired", { count: fired })}`
          : t("manual_form.success.created"),
      );
      navigate(`/analysis/${docId}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join("; ")
        : e?.message || t("manual_form.errors.generic");
      toast.error(`${t("manual_form.errors.submit_failed")} — ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const vendorOptions = useMemo(() =>
    vendors.map((v) => v.vendor_name).filter(Boolean), [vendors]);

  const addLine = () => {
    if (form.line_items.length >= 10) return;
    setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  };
  const removeLine = (i) => setForm((f) => ({
    ...f, line_items: f.line_items.filter((_, idx) => idx !== i),
  }));
  const setLine = (i, k, v) => setForm((f) => ({
    ...f,
    line_items: f.line_items.map((li, idx) => idx === i ? { ...li, [k]: v } : li),
  }));

  return (
    <form
      onSubmit={submit}
      className="p-6 max-w-4xl mx-auto space-y-6"
      data-testid="manual-invoice-page"
    >
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            type="button"
            onClick={() => fromDocId ? navigate(`/analysis/${fromDocId}`) : navigate("/upload")}
            data-testid="manual-invoice-back"
            className="text-[12px] text-zinc-500 hover:text-qenva-navy inline-flex items-center gap-1 mb-2"
          >
            <ArrowLeft size={11} />
            {t("manual_form.buttons.cancel")}
          </button>
          <p className="text-[11px] uppercase tracking-[0.2em] text-qenva-cat-invoice font-bold mb-1">
            <ReceiptText size={11} className="inline mr-1" />
            {t("manual_form.page_title")}
          </p>
          <h1 className="font-display font-bold text-qenva-navy text-2xl tracking-tight">
            {t("manual_form.page_title")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">{t("manual_form.page_subtitle")}</p>
        </div>
      </header>

      {/* Vendor section */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4">
        <h2 className="text-[13px] uppercase tracking-wider font-bold text-zinc-500">
          {t("manual_form.section_vendor")}
        </h2>
        <div>
          <label className="text-[12px] font-semibold text-qenva-navy block mb-1">
            {t("manual_form.labels.vendor_name")} <span className="text-qenva-cat-contract">*</span>
          </label>
          <input
            list="vendor-list"
            value={form.vendor_name}
            onChange={(e) => setField("vendor_name", e.target.value)}
            placeholder={t("manual_form.placeholders.vendor_name")}
            data-testid="manual-invoice-vendor-name"
            className={`w-full px-3 py-2 rounded-lg border text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 ${
              errors.vendor_name ? "border-qenva-cat-contract" : "border-zinc-300 focus:border-qenva-blue"
            }`}
          />
          <datalist id="vendor-list">
            {vendorOptions.map((v) => <option key={v} value={v} />)}
          </datalist>
          {errors.vendor_name && (
            <p className="text-[11.5px] text-qenva-cat-contract mt-1">{errors.vendor_name}</p>
          )}
          <p className="text-[11px] text-zinc-400 mt-1">{t("manual_form.labels.vendor_name_hint")}</p>
          {vendorProfileHint && (
            <p
              className="mt-1.5 text-[11.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 animate-fade-up"
              data-testid="vendor-profile-applied"
            >
              {t("manual_form.vendor_profile.applied", {
                label: vendorProfileHint.tax_label || t("manual_form.vendor_profile.no_tax"),
              })}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("manual_form.labels.invoice_number")}>
            <input
              value={form.invoice_number}
              onChange={(e) => setField("invoice_number", e.target.value)}
              placeholder={t("manual_form.placeholders.invoice_number")}
              data-testid="manual-invoice-number"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue"
            />
          </Field>
          <Field label={t("manual_form.labels.invoice_type")}>
            <select
              value={form.invoice_type}
              onChange={(e) => setField("invoice_type", e.target.value)}
              data-testid="manual-invoice-type"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue bg-white"
            >
              {INVOICE_TYPES.map((tk) => (
                <option key={tk} value={tk}>{t(`manual_form.invoice_types.${tk}`)}</option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      {/* Dates */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4">
        <h2 className="text-[13px] uppercase tracking-wider font-bold text-zinc-500">
          {t("manual_form.section_dates")}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("manual_form.labels.issue_date")}>
            <input
              type="date"
              value={form.issue_date}
              onChange={(e) => setField("issue_date", e.target.value)}
              data-testid="manual-invoice-issue-date"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue"
            />
          </Field>
          <Field
            label={t("manual_form.labels.due_date")}
            error={errors.due_date}
            required={form.payment_status !== "paid"}
          >
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setField("due_date", e.target.value)}
                data-testid="manual-invoice-due-date"
                className={`flex-1 px-3 py-2 rounded-lg border text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 ${
                  errors.due_date ? "border-qenva-cat-contract" : "border-zinc-300 focus:border-qenva-blue"
                }`}
              />
              <button
                type="button"
                onClick={() => setField("due_date", addDays(form.issue_date || todayIso(), 30))}
                data-testid="manual-invoice-due-plus30"
                className="px-2.5 py-1.5 rounded-md text-[11.5px] font-bold border border-zinc-300 text-zinc-600 hover:bg-zinc-50"
              >
                <Calendar size={10} className="inline mr-0.5" />
                {t("manual_form.buttons.shortcut_30")}
              </button>
              <button
                type="button"
                onClick={() => setField("due_date", addDays(form.issue_date || todayIso(), 60))}
                data-testid="manual-invoice-due-plus60"
                className="px-2.5 py-1.5 rounded-md text-[11.5px] font-bold border border-zinc-300 text-zinc-600 hover:bg-zinc-50"
              >
                {t("manual_form.buttons.shortcut_60")}
              </button>
            </div>
          </Field>
        </div>
      </section>

      {/* Amounts */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4">
        <header>
          <h2 className="text-[13px] uppercase tracking-wider font-bold text-zinc-500">
            {t("manual_form.section_amounts")}
          </h2>
        </header>

        {/* Multi-country tax selector (iter59) */}
        <div
          className="rounded-lg bg-zinc-50/70 border border-zinc-200/70 p-4 space-y-3"
          data-testid="manual-invoice-tax-selector"
        >
          <div className="flex items-center gap-2">
            <Globe2 size={13} className="text-qenva-cat-financing" />
            <span className="text-[11px] uppercase font-bold text-zinc-600 tracking-wider">
              {t("manual_form.tax_selector.title")}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 items-start">
            {/* Country select */}
            <div className="flex flex-col gap-1 min-w-[160px]">
              <label className="text-[10.5px] uppercase tracking-[0.16em] text-zinc-500 font-bold">
                {t("manual_form.tax_selector.country")}
              </label>
              <select
                value={taxCountry}
                onChange={(e) => onCountryChange(e.target.value)}
                data-testid="manual-invoice-tax-country"
                className="px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white text-[12.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue"
              >
                {TAX_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <p className="text-[10.5px] text-zinc-400 italic leading-snug">
                {t("manual_form.tax_selector.currency_hint")}
              </p>
            </div>

            {/* Subregime chips */}
            {country && country.subregimes.length > 0 && (
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-[10.5px] uppercase tracking-[0.16em] text-zinc-500 font-bold">
                  {t("manual_form.tax_selector.subregime")}
                </label>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="manual-invoice-tax-subregimes"
                >
                  {country.subregimes.map((sr) => {
                    const active = taxSubregimeId === sr.id;
                    // Keep the original test-id for the QC chip so the
                    // iter57/iter58 manual-invoice-compute-qc selector
                    // still resolves to the QC apply-tax control.
                    const testId = sr.id === "QC"
                      ? "manual-invoice-compute-qc"
                      : `manual-invoice-tax-${country.code}-${sr.id}`;
                    return (
                      <button
                        key={sr.id}
                        type="button"
                        onClick={() => applySubregime(sr)}
                        data-testid={testId}
                        className={`px-2.5 py-1 rounded-full text-[11.5px] font-bold border transition-colors ${
                          active
                            ? "bg-qenva-cat-financing text-white border-qenva-cat-financing"
                            : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100 hover:border-zinc-400"
                        }`}
                      >
                        {sr.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* USA exempt notice */}
          {country?.exempt_by_default && (
            <p
              className="text-[11.5px] text-zinc-600 italic"
              data-testid="manual-invoice-tax-usa-notice"
            >
              {t("manual_form.tax_selector.usa_exempt")}
            </p>
          )}

          {/* "Autre" — custom label + rate */}
          {country?.custom && (
            <div
              className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2 items-end"
              data-testid="manual-invoice-tax-custom"
            >
              <Field label={t("manual_form.tax_selector.custom_label")}>
                <input
                  type="text"
                  value={customTaxLabel}
                  onChange={(e) => setCustomTaxLabel(e.target.value)}
                  placeholder={t("manual_form.tax_selector.custom_label_placeholder")}
                  data-testid="manual-invoice-tax-custom-label"
                  maxLength={40}
                  className="w-full px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white text-[12.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue"
                />
              </Field>
              <Field label={t("manual_form.tax_selector.custom_rate")}>
                <input
                  type="number" step="0.01" min="0"
                  value={customTaxRate}
                  onChange={(e) => setCustomTaxRate(e.target.value)}
                  placeholder="%"
                  data-testid="manual-invoice-tax-custom-rate"
                  className="w-full px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white text-[12.5px] tabular-nums outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue"
                />
              </Field>
              <button
                type="button"
                onClick={applyCustomTax}
                data-testid="manual-invoice-tax-custom-apply"
                className="px-3 py-1.5 rounded-md text-[11.5px] font-bold border border-qenva-cat-financing/40 text-qenva-cat-financing hover:bg-qenva-cat-financing/10 inline-flex items-center gap-1"
              >
                <Calculator size={11} />
                {t("manual_form.tax_selector.custom_apply")}
              </button>
            </div>
          )}

          {/* Active selection summary (label + breakdown for QC/BC/SK/MB) */}
          {(form.tax_label || subregime?.breakdown) && (
            <div
              className="flex flex-col gap-0.5 text-[11.5px] text-zinc-600 pt-1 border-t border-zinc-200/80"
              data-testid="manual-invoice-tax-summary"
            >
              {form.tax_label && (
                <div className="font-bold text-zinc-800">
                  {form.tax_label}{" "}
                  {form.tax_rate !== "" && (
                    <span className="text-zinc-500 font-normal">
                      ({Number(form.tax_rate).toLocaleString("fr-CA", { maximumFractionDigits: 3 })} %)
                    </span>
                  )}
                </div>
              )}
              {subregime?.breakdown && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 tabular-nums">
                  {subregime.breakdown.map((b) => (
                    <span key={b.label}>
                      <span className="text-zinc-500">{b.label}:</span>{" "}
                      <span className="font-semibold text-zinc-800">{b.rate} %</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t("manual_form.labels.subtotal")}>
            <input
              type="number" step="0.01" min="0"
              value={form.subtotal}
              onChange={(e) => setField("subtotal", e.target.value)}
              data-testid="manual-invoice-subtotal"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] tabular-nums outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue"
            />
          </Field>
          <Field label={t("manual_form.labels.tax_amount")}>
            <input
              type="number" step="0.01" min="0"
              value={form.tax_amount}
              onChange={(e) => setField("tax_amount", e.target.value)}
              disabled={country?.exempt_by_default}
              data-testid="manual-invoice-tax-amount"
              className={`w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] tabular-nums outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue ${country?.exempt_by_default ? "bg-zinc-100 text-zinc-400" : ""}`}
            />
          </Field>
          <Field label={t("manual_form.labels.tax_rate")}>
            <input
              type="number" step="0.001" min="0"
              value={form.tax_rate}
              onChange={(e) => setField("tax_rate", e.target.value)}
              disabled={country?.exempt_by_default}
              data-testid="manual-invoice-tax-rate"
              className={`w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] tabular-nums outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue ${country?.exempt_by_default ? "bg-zinc-100 text-zinc-400" : ""}`}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("manual_form.labels.total_amount")} required error={errors.total_amount}>
            <input
              type="number" step="0.01" min="0.01"
              value={form.total_amount}
              onChange={(e) => setField("total_amount", e.target.value)}
              data-testid="manual-invoice-total-amount"
              className={`w-full px-3 py-2 rounded-lg border text-[14px] font-bold tabular-nums outline-none focus:ring-2 focus:ring-qenva-blue/30 ${
                errors.total_amount ? "border-qenva-cat-contract" : "border-zinc-300 focus:border-qenva-blue"
              }`}
            />
          </Field>
          <Field label={t("manual_form.labels.currency")}>
            <select
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
              data-testid="manual-invoice-currency"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue bg-white"
            >
              {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
      </section>

      {/* Status */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4">
        <h2 className="text-[13px] uppercase tracking-wider font-bold text-zinc-500">
          {t("manual_form.section_status")}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("manual_form.labels.payment_status")}>
            <select
              value={form.payment_status}
              onChange={(e) => setField("payment_status", e.target.value)}
              data-testid="manual-invoice-payment-status"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue bg-white"
            >
              {PAYMENT_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`payment_status.values.${s}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t("manual_form.labels.payment_method")}>
            <select
              value={form.payment_method}
              onChange={(e) => setField("payment_method", e.target.value)}
              data-testid="manual-invoice-payment-method"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13.5px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue bg-white"
            >
              <option value="">—</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{t(`manual_form.payment_methods.${m}`)}</option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      {/* Line items (collapsible) */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4" data-testid="manual-invoice-lines">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-[13px] uppercase tracking-wider font-bold text-zinc-500">
              {t("manual_form.section_lines")} <span className="normal-case font-medium text-zinc-400">{t("manual_form.section_lines_optional")}</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={addLine}
            disabled={form.line_items.length >= 10}
            data-testid="manual-invoice-add-line"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11.5px] font-bold border border-zinc-300 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40"
          >
            <Plus size={11} />
            {t("manual_form.buttons.add_line")}
          </button>
        </header>
        {form.line_items.length === 0 ? (
          <p className="text-[12px] text-zinc-400 italic">—</p>
        ) : (
          <div className="space-y-2">
            {form.line_items.map((li, i) => (
              <div key={i} data-testid={`manual-invoice-line-${i}`} className="grid grid-cols-12 gap-2 items-start">
                <input
                  value={li.description}
                  onChange={(e) => setLine(i, "description", e.target.value)}
                  placeholder={t("manual_form.placeholders.line_description")}
                  className="col-span-6 px-2.5 py-1.5 rounded-md border border-zinc-300 text-[13px] outline-none focus:ring-1 focus:ring-qenva-blue/30 focus:border-qenva-blue"
                />
                <input
                  type="number" step="0.01" placeholder={t("manual_form.labels.line_quantity")}
                  value={li.quantity}
                  onChange={(e) => setLine(i, "quantity", e.target.value)}
                  className="col-span-2 px-2.5 py-1.5 rounded-md border border-zinc-300 text-[13px] tabular-nums outline-none focus:ring-1 focus:ring-qenva-blue/30 focus:border-qenva-blue"
                />
                <input
                  type="number" step="0.01" placeholder={t("manual_form.labels.line_unit_price")}
                  value={li.unit_price}
                  onChange={(e) => setLine(i, "unit_price", e.target.value)}
                  className="col-span-2 px-2.5 py-1.5 rounded-md border border-zinc-300 text-[13px] tabular-nums outline-none focus:ring-1 focus:ring-qenva-blue/30 focus:border-qenva-blue"
                />
                <input
                  type="number" step="0.01" placeholder={t("manual_form.labels.line_total")}
                  value={li.line_total}
                  onChange={(e) => setLine(i, "line_total", e.target.value)}
                  className="col-span-1 px-2.5 py-1.5 rounded-md border border-zinc-300 text-[13px] tabular-nums outline-none focus:ring-1 focus:ring-qenva-blue/30 focus:border-qenva-blue"
                />
                <button
                  type="button"
                  onClick={() => removeLine(i)}
                  className="col-span-1 px-1.5 py-1.5 rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-qenva-cat-contract grid place-items-center"
                  aria-label={t("manual_form.buttons.remove_line")}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Notes */}
      <section className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4">
        <h2 className="text-[13px] uppercase tracking-wider font-bold text-zinc-500">
          {t("manual_form.section_notes")}
        </h2>
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder={t("manual_form.placeholders.notes")}
          data-testid="manual-invoice-notes"
          maxLength={2000}
          className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-[13px] outline-none focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue resize-none"
        />
      </section>

      {/* Submit bar */}
      <div className="flex items-center justify-end gap-3 pb-6">
        <button
          type="button"
          onClick={() => navigate("/upload")}
          className="px-4 py-2 rounded-lg font-bold text-zinc-600 hover:bg-zinc-50 transition-colors"
        >
          {t("manual_form.buttons.cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="manual-invoice-submit"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg font-bold text-white bg-qenva-navy hover:bg-[#13325A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {submitting
            ? <><Loader2 size={14} className="animate-spin" /> {t("manual_form.buttons.submitting")}</>
            : <><Save size={14} /> {t("manual_form.buttons.submit")}</>}
        </button>
      </div>
    </form>
  );
}

// Small inline helper component (kept here to avoid a one-off file)
function Field({ label, children, required, error }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-qenva-navy block mb-1">
        {label}{required ? <span className="text-qenva-cat-contract"> *</span> : null}
      </label>
      {children}
      {error && (
        <p className="text-[11.5px] text-qenva-cat-contract mt-1">{error}</p>
      )}
    </div>
  );
}
